import { noButtons, useElementSize } from '@byecode/ui'
import type { DragEndEvent, DragMoveEvent, DragStartEvent } from '@dnd-kit/core'
import { closestCenter, DndContext, DragOverlay, MouseSensor, useSensor, useSensors } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import type { PageAbstract, PageItem } from '@lighthouse/core'
import { floatPageTree, useAtomData } from '@lighthouse/shared'
import { find, findIndex } from 'rambda'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import VirtualList from 'react-tiny-virtual-list'
import { useLatest, useUpdateEffect, useWindowSize } from 'react-use'
import styled from 'styled-components'

import { pageTreeListAtom } from '@/atoms/page/state'
import type { CreatePagePayload, MovePagePayload } from '@/atoms/page/types'
import { findByPageByTree, getTreesFloatIds } from '@/atoms/utils/page'
import { DropIndicator } from '@/components/StyledGuideLine'
import { useCurrentLanguagePageList } from '@/hooks/usePage'

import PageItemEle from '../Item'
import { PageGroup } from '../Item/PageGroup'

interface DragListProps {
    list: PageItem[]
    focusPage?: string
    activePageId?: string
    openedMap: Record<string, boolean>
    style?: React.CSSProperties
    onUpdatePage?: (param: Partial<PageAbstract> & { id: string }) => Promise<boolean>
    onSetHomePage?: (param: Pick<PageAbstract, 'id' | 'isHome'>) => Promise<boolean>
    onRemovePage?: (id: string) => void
    onToLink?: (page: PageAbstract) => void
    onChangeEdit: (id: string) => void
    onCopyPage: (id: string, lang: string, groupId?: string) => Promise<string>
    onMovePage: (params: MovePagePayload) => void
    onCustomRoute?: (id: string) => void
    onAddPage: (v: CreatePagePayload) => void
    onToggleCollapse: (id: string, open?: boolean) => void
    onAllToggleCollapse: (open: boolean) => void
}

const SCxContainer = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    height: 100%;
`

const SCxVirtualList = styled(VirtualList)`
    ${noButtons('0px')}
`

type GuideRect = {
    top: number
    left: number
    width: number
    parentLeft: number
    initialScrollLeft: number
    position: 'current' | 'prev' | 'next'
    hidden: boolean
}
const DELTA_X = 24
export const DragList: React.FunctionComponent<DragListProps> = ({
    list,
    focusPage,
    activePageId,
    onMovePage,
    onAddPage,
    onToggleCollapse,
    onCopyPage,
    onAllToggleCollapse,
    ...rest
}) => {
    const [guideRect, setGuideRect] = useState<GuideRect>({
        top: 12,
        left: 0,
        width: 0,
        parentLeft: 0,
        initialScrollLeft: 0,
        position: 'current',
        hidden: false
    })
    const { height: windowHeight } = useWindowSize()
    const sensors = useSensors(useSensor(MouseSensor, { activationConstraint: { distance: 10 } }))
    const [activeId, setActiveId] = useState<string | null>(null)
    const containerRef = useRef<HTMLDivElement>(null)
    const pageGroups = useAtomData(pageTreeListAtom)
    const pageList = useCurrentLanguagePageList()
    const activePage = useMemo(() => (activeId ? findByPageByTree(pageGroups)(activeId) : undefined), [activeId, pageGroups])
    const activeChildIds = useMemo(() => (activePage?.type === 'GROUP' ? getTreesFloatIds(activePage.children, []) : []), [activePage])
    const [height, setHeight] = useState(0)
    const floatList = useMemo(() => floatPageTree(list, []), [list])

    useEffect(() => {
        const height = containerRef.current?.getBoundingClientRect().height ?? 0
        setHeight(height)
    }, [windowHeight])

    const activeIndex = useMemo(() => {
        const index = findIndex(item => item.id === activePageId, floatList)
        if (index === -1) {
            return
        }
        return index
    }, [activePageId, floatList])

    const handleDragStart = useCallback((event: DragStartEvent) => {
        setActiveId(event.active.id.toString())
    }, [])

    const handleDragEnd = useCallback(
        (event: DragEndEvent) => {
            const { active, over } = event
            if (!over) {
                return
            }
            onMovePage({
                id: active.id.toString(),
                oldId: active.id.toString(),
                newId: over.id.toString(),
                position: guideRect.position
            })
            setActiveId('')
        },
        [guideRect.position, onMovePage]
    )

    const handDragMove = useCallback(
        (ev: DragMoveEvent) => {
            const { over, active, collisions, delta } = ev
            const tableTop = containerRef.current?.getBoundingClientRect().top ?? 0
            const activeCollision = find(item => item.id === active.id, collisions ?? [])
            const activeHeight: number = activeCollision?.data?.droppableContainer.rect.current.height
            const activeIndex = findIndex(item => item.id === active.id, floatList)
            const overIndex = findIndex(item => item.id === (over?.id ?? ''), floatList)
            requestAnimationFrame(() => {
                setGuideRect(rect => {
                    const baseTop = over?.rect.top ?? 0
                    const baseOffset = activeIndex < overIndex ? (over?.rect.height ?? 0) - activeHeight : 0
                    // 12 为pageTreeListWrapper的内上边距
                    const top = baseTop - tableTop + baseOffset + 12
                    if (!floatList[overIndex]) {
                        return { ...rect, top, left: 0 }
                    }
                    const offsetIndex = activeIndex >= overIndex ? -1 : 0
                    const prevItem =
                        floatList[overIndex + offsetIndex]?.id === active.id ? floatList[overIndex] : floatList[overIndex + offsetIndex]
                    const activeItem = floatList[activeIndex]
                    let position: GuideRect['position'] = 'current'

                    if (prevItem?.type === 'GROUP') {
                        // if ((activeItem?.level ?? 0) > (prevItem?.level ?? 0)) {
                        //     position = 'prev'
                        // }
                        if (delta.x > DELTA_X) {
                            position = 'current'
                        }
                        if (delta.x < DELTA_X) {
                            position = 'prev'
                        }
                        // if ((activeItem?.level ?? 0) === (prevItem?.level ?? 0) && activeItem?.parentId === prevItem?.parentId) {
                        //     position = 'next'
                        // }
                    }

                    if (prevItem?.type === 'COMMON') {
                        if ((activeItem?.level ?? 0) > (prevItem?.level ?? 0)) {
                            position = 'prev'
                        }
                        if ((activeItem?.level ?? 0) < (prevItem?.level ?? 0) && prevItem.parentId) {
                            position = 'next'
                        }
                    }

                    const positionLevel = prevItem?.type === 'GROUP' && delta.x > DELTA_X ? 1 : 0
                    return {
                        ...rect,
                        top: top - 12,
                        left: 24 * positionLevel + (prevItem?.level ?? 0) * 20 + 24,
                        position,
                        hidden: activeChildIds.includes(prevItem?.id ?? '')
                    }
                })
            })
        },
        [activeChildIds, floatList]
    )

    const virtualListRef = useRef<VirtualList>(null)
    const scrollTopRef = useLatest((activeIndex ?? 0) * 40)
    const activePageRef = useLatest(floatList[activeIndex ?? 0])
    const prevPageListRef = useRef(pageList.length)
    useUpdateEffect(() => {
        const isTopAddPage = activePageRef.current && !activePageRef.current?.parentId && pageList.length > prevPageListRef.current
        const isDeleteJumpHomePage =
            pageList.length < prevPageListRef.current && activePageRef?.current.type === 'COMMON' && activePageRef.current?.isHome
        if (isDeleteJumpHomePage || isTopAddPage) {
            onAllToggleCollapse(true)
            prevPageListRef.current = pageList.length
            requestAnimationFrame(() => {
                virtualListRef.current?.scrollTo(scrollTopRef.current)
            })
        }
    }, [onAllToggleCollapse, scrollTopRef, pageList.length, activePageRef])

    const renderItem = useCallback(
        (item: PageItem, style?: React.CSSProperties) => {
            if (activeChildIds.includes(item.id)) {
                return null
            }
            return item.type === 'GROUP' ? (
                <PageGroup
                    style={style}
                    key={item.id}
                    tree={item}
                    focusPage={focusPage}
                    onMovePage={onMovePage}
                    onAdd={onAddPage}
                    onToggleCollapse={onToggleCollapse}
                    activeId={activePageId}
                    onCopyPage={onCopyPage}
                    {...rest}
                />
            ) : (
                <PageItemEle
                    key={item.id}
                    style={style}
                    id={item.id}
                    level={item.level ?? 0}
                    isEdit={focusPage === item.id}
                    onMovePage={onMovePage}
                    activeId={activePageId}
                    onCopyPage={(id, lang) => onCopyPage(id, lang, lang === item.language ? item.parentId : undefined)}
                    {...rest}
                />
            )
        },
        [activeChildIds, activePageId, focusPage, onAddPage, onToggleCollapse, onCopyPage, onMovePage, rest]
    )

    return (
        <SCxContainer ref={containerRef}>
            {height > 0 && (
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragStart={handleDragStart}
                    onDragMove={handDragMove}
                    onDragEnd={handleDragEnd}
                >
                    <SortableContext items={floatList.map(v => v.id)} strategy={verticalListSortingStrategy}>
                        <SCxVirtualList
                            ref={virtualListRef}
                            width="100%"
                            height={height}
                            itemCount={floatList.length}
                            itemSize={40}
                            overscanCount={2}
                            renderItem={({ index, style }) => {
                                const item = floatList[index]
                                return renderItem(item, style)
                            }}
                        />

                        {/* {floatList.map(item => renderItem(item))} */}
                    </SortableContext>
                    {createPortal(
                        <DragOverlay style={{ opacity: 0.8 }}>{activePage && renderItem(activePage)}</DragOverlay>,
                        document.body
                    )}
                    <DropIndicator
                        rect={guideRect}
                        style={{
                            right: 0,
                            width: 'auto'
                        }}
                    />
                </DndContext>
            )}
        </SCxContainer>
    )
}
